/**
 * The upload queue.
 *
 * The Capture screen tells estimators "works with no signal — shots queue and
 * upload when you are back inside". This is the code that makes that true. It
 * matters more than it sounds: a collision shop is a metal building, the car is
 * often at the back of it, and the moment an estimator loses a photograph they
 * walked around a car to take is the moment they stop trusting the app.
 *
 * The queue logic here is pure and synchronous. Persistence is an interface, so
 * the decisions — what to retry, how long to wait, when to give up — are
 * testable without IndexedDB, a network, or a clock.
 */

export type QueueState = 'pending' | 'uploading' | 'done' | 'failed';

export interface QueueItem {
  id: string;
  jobId: string;
  shopId: string;
  slot: string;
  /** The downscaled bytes. Held until the upload succeeds. */
  blob: Blob;
  width: number;
  height: number;
  takenAt: string;
  state: QueueState;
  attempts: number;
  /** Why the last attempt failed, shown to the estimator verbatim. */
  lastError: string | null;
  /** Earliest time the next attempt may run, as a timestamp. */
  nextAttemptAt: number;
}

/**
 * Give up after this many attempts.
 *
 * Not infinite: a photograph that has failed six times is not going to succeed
 * on the seventh, and a queue that retries forever looks identical to one that
 * is working. The estimator is told, and can retake the shot.
 */
export const MAX_ATTEMPTS = 6;

/**
 * Backoff, in milliseconds: 2s, 8s, 30s, 2m, 5m.
 *
 * Deliberately front-loaded. Most failures in a workshop are a few seconds of
 * no signal while someone walks past a steel door, not an outage — so the first
 * retry should be quick enough that the estimator never notices.
 */
export const BACKOFF_MS = [2_000, 8_000, 30_000, 120_000, 300_000];

export function backoffFor(attempts: number): number {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)]!;
}

/** What the queue should do next, given the time and whether there is a network. */
export function nextToUpload(
  items: QueueItem[],
  now: number,
  online: boolean,
): QueueItem | null {
  if (!online) return null;
  // Oldest first. An estimator expects the shots to arrive in the order they
  // walked around the car, and a queue that reorders them looks broken even
  // when every photograph eventually lands.
  const ready = items
    .filter((i) => i.state === 'pending' && i.nextAttemptAt <= now)
    .sort((a, b) => a.takenAt.localeCompare(b.takenAt));
  return ready[0] ?? null;
}

export function markUploading(item: QueueItem): QueueItem {
  return { ...item, state: 'uploading' };
}

export function markDone(item: QueueItem): QueueItem {
  return { ...item, state: 'done', lastError: null };
}

/**
 * Record a failure and decide whether to try again.
 *
 * `retryable` comes from the upload layer, which knows the difference between
 * "no signal" and "this file will never be accepted". Retrying the second kind
 * six times wastes five minutes and still ends in the same place.
 */
export function markFailed(
  item: QueueItem,
  message: string,
  retryable: boolean,
  now: number,
): QueueItem {
  const attempts = item.attempts + 1;
  const exhausted = !retryable || attempts >= MAX_ATTEMPTS;
  return {
    ...item,
    attempts,
    lastError: message,
    state: exhausted ? 'failed' : 'pending',
    nextAttemptAt: exhausted ? item.nextAttemptAt : now + backoffFor(attempts),
  };
}

export interface QueueSummary {
  pending: number;
  uploading: number;
  failed: number;
  done: number;
  /** Bytes still waiting to leave the device. */
  bytesWaiting: number;
}

export function summarise(items: QueueItem[]): QueueSummary {
  const s: QueueSummary = { pending: 0, uploading: 0, failed: 0, done: 0, bytesWaiting: 0 };
  for (const i of items) {
    s[i.state] += 1;
    if (i.state === 'pending' || i.state === 'uploading') s.bytesWaiting += i.blob.size;
  }
  return s;
}

/**
 * What the estimator is told.
 *
 * Deliberately not a spinner. Someone standing at a car needs to know whether
 * they can walk away, and "3 waiting for signal" answers that where a spinner
 * does not.
 */
export function queueMessage(s: QueueSummary, online: boolean): string | null {
  if (s.failed > 0) {
    return `${s.failed} photo${s.failed === 1 ? '' : 's'} could not be sent. `
         + 'Retake them before you issue this estimate.';
  }
  const waiting = s.pending + s.uploading;
  if (waiting === 0) return null;
  if (!online) {
    return `${waiting} photo${waiting === 1 ? '' : 's'} saved on this device. `
         + 'They will send themselves when you have signal — you can keep working.';
  }
  return `Sending ${waiting} photo${waiting === 1 ? '' : 's'}…`;
}

/** Persistence, so a closed tab or a dead battery does not lose the queue. */
export interface QueueStore {
  all(): Promise<QueueItem[]>;
  put(item: QueueItem): Promise<void>;
  remove(id: string): Promise<void>;
}
